import { SidebarIcon } from "@phosphor-icons/react/dist/csr/Sidebar";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { useSidebar } from "@volca/ui/components/ui/sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { Heading, Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import { useNavColumn } from "./nav-column-state";
import {
  NAV_PANEL_DEFAULT,
  NAV_PANEL_MAX,
  NAV_PANEL_MIN,
} from "./nav-panel-width";
import { railRowGroups, type RailEntry } from "./rail-model";
import { useShellColumnOpen } from "./shell-frame";
import { NavPanelResize } from "./sidebar-nav-resize";
import { PanelRow } from "./sidebar-panel-row";

const PanelSections = ({
  entry,
  onNavigate,
}: {
  entry: RailEntry;
  onNavigate?: () => void;
}) => (
  <nav aria-label={entry.label}>
    {railRowGroups(entry.sections).map((group, index) => (
      <Stack key={group.label ?? index} gap={0.5} className="pb-1">
        {group.label && (
          <Text variant="caption" tone="muted" className="px-2 pt-2">
            {group.label}
          </Text>
        )}
        <ul>
          {group.rows.map((row) => (
            <PanelRow
              key={`${row.group ?? ""}:${row.to}`}
              row={row}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      </Stack>
    ))}
  </nav>
);

const panelMotion =
  "motion-safe:transition-[width] motion-safe:duration-[var(--sidebar-duration)] motion-safe:ease-[var(--sidebar-ease)]";

const revealClass =
  "opacity-50 transition-[opacity,background-color] duration-150 ease-[var(--sidebar-ease-out)] group-hover/panel:opacity-100 focus-visible:opacity-100";

const NavPanelBody = ({
  entry,
  onNavigate,
  onCollapse,
  className,
}: {
  entry: RailEntry;
  onNavigate?: () => void;
  onCollapse?: () => void;
  className?: string;
}) => (
  <Stack className={cn("group/panel min-h-0", className)}>
    <Stack
      direction="row"
      align="center"
      gap={1}
      className={cn("h-10 shrink-0 ps-3", {
        "pe-3": onCollapse,
        "pe-2": !onCollapse,
      })}
    >
      <Heading
        level={2}
        variant="label"
        tone="muted"
        truncate
        className="min-w-0 flex-1"
      >
        {entry.label}
      </Heading>
      {onCollapse && (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Hide ${entry.label}`}
                onClick={onCollapse}
                className={revealClass}
              />
            }
          >
            <SidebarIcon />
          </TooltipTrigger>
          <TooltipContent side="right">Hide {entry.label}</TooltipContent>
        </Tooltip>
      )}
    </Stack>
    <Stack className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-1">
      <PanelSections entry={entry} onNavigate={onNavigate} />
    </Stack>
  </Stack>
);

const NavPanel = ({ entry }: { entry: RailEntry }) => {
  const column = useNavColumn();
  const { snap } = column;
  const width = column.width.value;
  const open = !column.hidden;
  const [resizing, setResizing] = useState(false);
  const [liveWidth, setLiveWidth] = useState<number | null>(null);
  const shown = liveWidth ?? (open ? width : 0);
  const frozen = resizing || snap;
  const extended = useShellColumnOpen();

  return (
    <div
      id="nav-panel"
      style={{ width: shown, transition: frozen ? "none" : undefined }}
      className={cn("relative min-h-0 shrink-0 self-stretch", {
        [panelMotion]: !frozen,
      })}
    >
      <div
        inert={open ? undefined : true}
        style={{ width: shown, transition: frozen ? "none" : undefined }}
        className={cn("h-full overflow-hidden bg-surface", {
          "rounded-s-xl": extended && shown > 0,
          "rounded-xl": !extended && shown > 0,
          [panelMotion]: !frozen,
        })}
      >
        <Stack style={{ width }} className="h-full min-h-0">
          <NavPanelBody
            entry={entry}
            onCollapse={open ? column.hide : undefined}
            onNavigate={entry.onNavigate}
            className="h-full"
          />
        </Stack>
      </div>
      <NavPanelResize
        width={width}
        min={NAV_PANEL_MIN}
        max={NAV_PANEL_MAX}
        defaultWidth={NAV_PANEL_DEFAULT}
        collapsed={!open}
        onWidth={column.width.set}
        onCommit={column.width.commit}
        onCollapse={column.hide}
        onExpand={column.show}
        onLiveWidth={setLiveWidth}
        onResizing={setResizing}
      />
    </div>
  );
};

const NavPreview = () => {
  const column = useNavColumn();
  const { preview } = column;
  if (!preview.current) return null;
  const { entry, pin } = preview.current;
  return (
    <Stack
      onMouseEnter={preview.hold}
      onMouseLeave={preview.hideSoon}
      style={{ width: column.width.value }}
      className={cn(
        "absolute z-20 h-[66.666dvh] overflow-hidden rounded-xl bg-surface shadow-raised",
        {
          "start-0 top-0": pin === "top",
          "start-0 bottom-0": pin === "bottom",
        },
      )}
    >
      <NavPanelBody
        entry={entry}
        onNavigate={() => {
          entry.onNavigate?.();
          preview.close();
        }}
        className="min-h-0 w-full flex-1"
      />
    </Stack>
  );
};

export const NavColumn = () => {
  const { isMobile } = useSidebar();
  const column = useNavColumn();
  if (isMobile) return null;
  return (
    <>
      {column.active && <NavPanel entry={column.active} />}
      <NavPreview />
    </>
  );
};
